import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useDataProvider, useGetList, useNotify } from 'react-admin';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useBrsCreateStyles } from './BrsCreateStyles';
import { NewRequestSection } from './components/NewRequestSection';
import { BroadcastRequestsSection } from './components/BroadcastRequestsSection';
import { DestinationsSection } from './components/DestinationsSection';
import { FormFooter } from './components/FormFooter';
import { WeeklyRotationSummary } from './components/WeeklyRotationSummary';
import {
  computeDailyTotals,
  dayRotationsToSchedule,
  DAYS,
  scheduleToDayRotations,
} from './components/splitWeekUtils';
import { validateOriginalsPresent } from './brsValidation';
import PDFPreviewModal from '../../components/PDF/PDFPreviewModal';
import { getLatestJobSpots } from '../../helpers';

let nextSpotId = 1;

const extractApiErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.length > 0) return detail;
  return error?.message || 'Unknown error';
};

// Convert a spot returned by the latest-job-spots endpoint into the form spot
// shape. The endpoint already resolves the most recent job and its latest
// flight week, so there is no cross-job aggregation or CatDV re-lookup here.
const endpointSpotToSpot = (s) => {
  const fw = s?.flight_week || {};
  const dr = Array.isArray(fw.day_rotations) ? fw.day_rotations : [];
  const originals =
    Array.isArray(s?.isciasset?.original_iscis) && s.isciasset.original_iscis.length > 0
      ? s.isciasset.original_iscis
      : [{}];
  return {
    _id: nextSpotId++,
    // Real broadcast-request id — the update submit keys flight-week creation
    // and the BR update off it. _id is only a client-side list key.
    id: s?.id || '',
    isPrePopulated: true,
    isciasset: {
      isci: s?.isciasset?.isci || s?.isci || '',
      sigma_encoding: s?.isciasset?.sigma_encoding || 'CADENT',
      bvs_encoding: s?.isciasset?.bvs_encoding || 'NO',
      include_sd: !!s?.isciasset?.include_sd,
      duration: s?.isciasset?.duration || 0,
      original_iscis: originals,
      exists: true,
    },
    title: s?.title || '',
    start_date: fw.start_date || '',
    end_date: fw.end_date || '',
    unit: fw.unit || '',
    rotation: fw.rotation || '',
    traffic_only: s?.traffic_only === true,
    copy_id: s?.copy_id || s?.isci || null,
    split_week_schedule: dayRotationsToSchedule(dr),
  };
};

const createSpot = (overrides = {}, scheduleDefault = 100) => ({
  _id: nextSpotId++,
  isciasset: {
    isci: '',
    sigma_encoding: 'CADENT',
    bvs_encoding: 'NO',
    include_sd: false,
    duration: 0,
    original_iscis: [{}],
  },
  title: '',
  start_date: '',
  end_date: '',
  unit: '',
  rotation: '',
  traffic_only: false,
  copy_id: null,
  split_week_schedule: {
    mon: scheduleDefault, tue: scheduleDefault, wed: scheduleDefault,
    thu: scheduleDefault, fri: scheduleDefault, sat: scheduleDefault, sun: scheduleDefault,
  },
  ...overrides,
});

export const TrafficInstructionEdit = (props) => {
  const classes = useBrsCreateStyles();
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const brsJobId = props.match?.params?.id;

  const [brsJobData, setBrsJobData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    request_id: '',
    estimate_id: '',
    slate_type: 'CADENT',
    market_type: 'LOCAL',
    inventory_source_id: '',
    unit_rotation: 'unit',
    flight_start_date: '',
    flight_end_date: '',
    flight_weeks: '',
  });
  const [spots, setSpots] = useState([]);
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [antennaSDActive, setAntennaSDActive] = useState(false);
  const [brandChoices, setBrandChoices] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [trafficInstructionsKey, setTrafficInstructionsKey] = useState('');
  const [pdfFileName, setPdfFileName] = useState('');
  const [attachedPdfFile, setAttachedPdfFile] = useState(null);

  // Zero-day acknowledgements (BRS split-week). Holds the day keys
  // ('mon'..'sun') whose 0 daily total the operator has explicitly confirmed
  // by clicking the day's "Daily Total" cell. A 0-total day blocks submission
  // until it appears here. Applies to both Rotation and Unit modes.
  const [acknowledgedZeroDays, setAcknowledgedZeroDays] = useState([]);

  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  });

  const { data: inventorySourceData } = useGetList(
    'inventorysource',
    { page: 1, perPage: 500 },
    { field: 'name', order: 'ASC' },
  );
  const inventorySources = useMemo(
    () => Object.values(inventorySourceData || {}),
    [inventorySourceData],
  );

  useEffect(() => {
    dataProvider
      .getList('brand', {
        pagination: { page: 1, perPage: 500 },
        sort: { field: 'name', order: 'ASC' },
      })
      .then(({ data }) => setBrandChoices(data.sort((a, b) => a.name.localeCompare(b.name))))
      .catch((err) => console.error('Failed to load brand choices:', err));
  }, [dataProvider]);

  // Hydrate the form from the BRS job + its existing broadcast requests +
  // active destinations. Pre-populated spots use isPrePopulated so the card
  // skips re-classification and locks down the title.
  useEffect(() => {
    if (!brsJobId) return;
    setLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const { data: job } = await dataProvider.getOne('brsjob', { id: brsJobId });
        if (cancelled || !job) return;

        // Pre-populate from the LAST job sent only, not every ISCI ever sent.
        // The backend resolves the most recent job by request_id, falling back
        // to estimate_id, and returns its spots (latest flight week per spot).
        const latest = await getLatestJobSpots({
          requestId: job.request_id,
          estimateId: job.estimate_id,
        });

        if (cancelled) return;
        setBrsJobData(job);
        // Auto-detect unit_rotation from spot data: if any loaded spot has
        // a non-empty `unit` field, the job was created in unit mode; if any
        // has a non-empty `rotation`, it's rotation mode. Fall back to the
        // brsjob value or default 'unit'.
        const loadedSpots = (latest?.spots || []).map(endpointSpotToSpot);
        let detectedMode = job.unit_rotation || 'unit';
        if (!job.unit_rotation) {
          const hasUnit = loadedSpots.some((s) => s.unit && String(s.unit).trim() !== '');
          const hasRotation = loadedSpots.some((s) => s.rotation && String(s.rotation).trim() !== '');
          if (hasRotation) detectedMode = 'rotation';
          else if (hasUnit) detectedMode = 'unit';
        }
        setFormData({
          request_id: job.request_id || '',
          estimate_id: job.estimate_id || '',
          slate_type: job.slate_type || 'CADENT',
          market_type: job.market_type || 'LOCAL',
          inventory_source_id: job.inventory_source_id || '',
          unit_rotation: detectedMode,
          flight_start_date: job.flight_start_date || '',
          flight_end_date: job.flight_end_date || '',
          flight_weeks: job.flight_weeks ? String(job.flight_weeks) : '',
        });
        setSpots(loadedSpots);
        setSelectedDestinations(
          (job.destinations || [])
            .filter((d) => d.active)
            .map((d) => ({
              id: d.destination.id,
              name: d.destination.name,
              call_letters: d.destination.call_letters,
            })),
        );
      } catch (err) {
        if (!cancelled) {
          notify(`Failed to load traffic update: ${extractApiErrorMessage(err)}`, 'error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brsJobId, dataProvider, notify]);

  // The brsjob record has no inventory_source_id — the table stores only
  // inventory_source_name (create resolves id -> name and persists the name).
  // Resolve the id back from the loaded inventory sources so the select
  // populates; runs whenever both the job and the source list are available.
  useEffect(() => {
    const name = brsJobData?.inventory_source_name;
    if (!name || inventorySources.length === 0) return;
    const match = inventorySources.find((src) => src.name === name);
    if (!match) return;
    setFormData((prev) =>
      prev.inventory_source_id ? prev : { ...prev, inventory_source_id: match.id },
    );
  }, [brsJobData, inventorySources]);

  const handleFormDataChange = useCallback((newFormData) => {
    setFormData((prevFormData) => {
      const modeChanged = newFormData.unit_rotation !== prevFormData.unit_rotation;
      if (modeChanged) {
        const newDefault = newFormData.unit_rotation === 'unit' ? 1 : 100;
        setSpots((prev) =>
          prev.map((spot) => ({
            ...spot,
            split_week_schedule: {
              mon: newDefault, tue: newDefault, wed: newDefault,
              thu: newDefault, fri: newDefault, sat: newDefault, sun: newDefault,
            },
          })),
        );
      }
      return newFormData;
    });
  }, []);

  // Mirrors BrsCreate's payload shape — wraps split_week_schedule into the
  // day_rotations[] the backend persists alongside legacy fields.
  const buildPayload = () => {
    // Per-spot unit/rotation mirrors the top-level mode (see the dropdown).
    // Derive from formData.unit_rotation rather than the hydrated spot fields
    // so toggling the mode actually changes what gets persisted — otherwise
    // a job saved in rotation mode keeps rotation="100" forever even after
    // switching to Unit.
    const mode = formData.unit_rotation;
    const spotUnit = mode === 'unit' ? '1' : '';
    const spotRotation = mode === 'rotation' ? '100' : '';

    const brs_spots = spots.map((spot) => {
      const result = {
        // Broadcast-request id the backend keys the flight-week/BR update off.
        id: spot.id || '',
        start_date: spot.start_date || null,
        end_date: spot.end_date || null,
        title: spot.title || '',
        traffic_only: spot.traffic_only === true,
        copy_id: spot.copy_id || (spot.traffic_only ? spot.isciasset?.isci || null : null),
        split_week_schedule: spot.split_week_schedule || {
          mon: 100, tue: 100, wed: 100, thu: 100, fri: 100, sat: 100, sun: 100,
        },
        day_rotations: scheduleToDayRotations(spot.split_week_schedule),
        isciasset: {
          isci: spot.isciasset?.isci || '',
          sigma_encoding: spot.isciasset?.sigma_encoding || 'CADENT',
          bvs_encoding: spot.isciasset?.bvs_encoding || 'NO',
          include_sd: !!spot.isciasset?.include_sd,
          duration: spot.isciasset?.duration || 0,
          // Brand is editable on traffic updates (BRS-FORM-P1 / SOW): send the
          // original ISCIs — with the ids the pre-populate supplied — so the
          // backend persists brand name/code edits. Rows without an isci are
          // empty placeholders.
          original_iscis: (spot.isciasset?.original_iscis || []).filter((oi) => oi.isci),
        },
      };
      result.unit = spotUnit;
      result.rotation = spotRotation;
      if (spot.isciasset?.exists !== undefined) {
        result.isciasset.exists = spot.isciasset.exists;
      }
      if (spot.isPrePopulated) result.is_traffic_update = true;
      return result;
    });

    return {
      ...formData,
      id: brsJobId,
      brs_spots,
      destinations_values: selectedDestinations.map((d) => d.id),
    };
  };

  const validate = () => {
    if (spots.length === 0) {
      notify('At least one broadcast request is required', 'warning');
      return false;
    }
    for (let i = 0; i < spots.length; i++) {
      if (!spots[i].isciasset?.isci) {
        notify(`ISCI is required for spot ${i + 1}`, 'warning');
        return false;
      }
    }
    if (selectedDestinations.length === 0) {
      notify('Please add at least one destination', 'warning');
      return false;
    }
    // Traffic update can only re-send or drop destinations already on the job;
    // adding one is an Add-on operation. Mirror the backend rejection here so
    // the operator finds out before generating/sending the PDF.
    const jobDestIds = new Set(
      (brsJobData?.destinations || []).map((d) => d.destination?.id),
    );
    const addedDests = selectedDestinations.filter((d) => !jobDestIds.has(d.id));
    if (addedDests.length > 0) {
      notify(
        `${addedDests.map((d) => d.name).join(', ')} is not on this job. ` +
          'Adding a destination is an Add-on, not a traffic update.',
        'warning',
      );
      return false;
    }
    const originalsError = validateOriginalsPresent(spots);
    if (originalsError) {
      notify(originalsError, 'warning');
      return false;
    }
    return true;
  };

  const nextTrafficInstructionVersion = () =>
    ((brsJobData?.trafficinstructions?.length || 0) + 1).toString();

  const handleGeneratePdf = async () => {
    if (!validate()) return;
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('token');
      const spotsWithDates = spots.map((s) => ({
        isci: s.isciasset?.isci,
        start_date: s.start_date || null,
        end_date: s.end_date || null,
        // Carry the in-memory split-week schedule so the preview reflects
        // the operator's current mode/edits instead of the persisted rows.
        day_rotations: scheduleToDayRotations(s.split_week_schedule),
      }));
      const response = await axios.post(
        `${window.REACT_APP_API_URL}/api/v1/preview-traffic-instructions-update`,
        {
          brs_job_id: brsJobId,
          brs_spots: spotsWithDates,
          unit_rotation: formData.unit_rotation,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.url) {
        setPdfUrl(response.data.url);
        setTrafficInstructionsKey(response.data.key);
        setPdfFileName(response.data.filename);
        setPdfModalOpen(true);
      } else {
        notify('No PDF URL received', 'error');
      }
    } catch (error) {
      notify(`Error generating traffic instructions: ${extractApiErrorMessage(error)}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendUpdate = async (keyOverride, fileNameOverride) => {
    const token = localStorage.getItem('token');
    const payload = buildPayload();
    payload.trafficinstructions_key = keyOverride ?? trafficInstructionsKey ?? '';
    payload.file_name = fileNameOverride ?? pdfFileName ?? '';
    payload.filepath = payload.trafficinstructions_key;
    payload.traffic_instruction_version = nextTrafficInstructionVersion();
    await axios.post(
      `${window.REACT_APP_API_URL}/api/v1/update-traffic-instructions`,
      { brs_job: payload },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      setIsSubmitting(true);
      let key = trafficInstructionsKey || '';
      let fileName = pdfFileName || '';
      if (attachedPdfFile) {
        const token = localStorage.getItem('token');
        const fd = new FormData();
        fd.append('file', attachedPdfFile);
        const uploadResp = await axios.post(
          `${window.REACT_APP_API_URL}/upload-traffic-instructions`,
          fd,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          },
        );
        key = uploadResp.data?.key || '';
        fileName = attachedPdfFile.name;
      }
      await sendUpdate(key, fileName);
      notify('Traffic instruction update sent successfully', 'success');
      window.location.href = '#/brs';
    } catch (error) {
      notify(`Error updating traffic instructions: ${extractApiErrorMessage(error)}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePdfSend = async () => {
    if (!validate()) return;
    try {
      setIsSubmitting(true);
      await sendUpdate();
      setPdfModalOpen(false);
      notify('Traffic instruction update sent successfully', 'success');
      window.location.href = '#/brs';
    } catch (error) {
      notify(`Error updating traffic instructions: ${extractApiErrorMessage(error)}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAttachPdf = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf';
    fileInput.style.display = 'none';
    document.body.appendChild(fileInput);
    fileInput.onchange = (event) => {
      const file = event.target.files?.[0];
      if (file) setAttachedPdfFile(file);
      document.body.removeChild(fileInput);
    };
    fileInput.click();
  };

  const handleRemoveAttachedPdf = () => setAttachedPdfFile(null);

  const handleCancel = () => {
    window.location.href = '#/brs';
  };

  // Submit gate (BRS split-week). In Rotation mode every day column must total
  // exactly 0% or 100% across all spots:
  //   - A total over 100% or a partial split (1..99%) blocks Generate PDF /
  //     Submit Job until the operator fixes it.
  //   - A total of exactly 0% (nothing airing) is allowed, but the operator
  //     must first click that day's "Daily Total" cell to confirm awareness;
  //     until then the buttons stay disabled.
  // In Unit mode the 100% constraint doesn't apply, but zero-day confirmation
  // is still required — operators must acknowledge days with 0 total units.
  const isRotationMode = formData.unit_rotation === 'rotation';
  const isUnitMode = formData.unit_rotation === 'unit';
  const dailyTotals = computeDailyTotals(spots);
  // zeroDaySig runs for both modes so zero-day acks are tracked regardless.
  const zeroDaySig = DAYS.filter((d) => dailyTotals[d.key] === 0)
    .map((d) => d.key)
    .join(',');

  // Drop acknowledgements for days that are no longer 0 so a day that leaves
  // and later re-enters 0 must be confirmed again.
  useEffect(() => {
    const stillZero = zeroDaySig.split(',').filter(Boolean);
    setAcknowledgedZeroDays((prev) => {
      const next = prev.filter((k) => stillZero.includes(k));
      return next.length === prev.length ? prev : next;
    });
  }, [zeroDaySig]);

  const toggleZeroDayAck = useCallback((dayKey) => {
    setAcknowledgedZeroDays((prev) =>
      prev.includes(dayKey) ? prev.filter((k) => k !== dayKey) : [...prev, dayKey],
    );
  }, []);

  let submitBlocked = false;
  let submitBlockedReason;
  if (isRotationMode) {
    const over100 = DAYS.filter((d) => dailyTotals[d.key] > 100);
    const partial = DAYS.filter((d) => dailyTotals[d.key] > 0 && dailyTotals[d.key] < 100);
    const unconfirmedZero = DAYS.filter(
      (d) => dailyTotals[d.key] === 0 && !acknowledgedZeroDays.includes(d.key),
    );
    if (over100.length > 0) {
      submitBlocked = true;
      submitBlockedReason = `Daily total exceeds 100% on ${over100
        .map((d) => d.label)
        .join(', ')}. Each day must total exactly 100%.`;
    } else if (partial.length > 0) {
      submitBlocked = true;
      submitBlockedReason = `Daily total must equal 100% on ${partial
        .map((d) => d.label)
        .join(', ')} (currently under 100%).`;
    } else if (unconfirmedZero.length > 0) {
      submitBlocked = true;
      const labels = unconfirmedZero.map((d) => d.label).join(', ');
      submitBlockedReason = `Click the 0% Daily Total for ${labels} to confirm ${
        unconfirmedZero.length > 1 ? 'those days are' : 'that day is'
      } intentionally empty.`;
    }
  } else if (isUnitMode) {
    const unconfirmedZero = DAYS.filter(
      (d) => dailyTotals[d.key] === 0 && !acknowledgedZeroDays.includes(d.key),
    );
    if (unconfirmedZero.length > 0) {
      submitBlocked = true;
      const labels = unconfirmedZero.map((d) => d.label).join(', ');
      submitBlockedReason = `Click the 0-unit Daily Total for ${labels} to confirm ${
        unconfirmedZero.length > 1 ? 'those days have' : 'that day has'
      } no units intentionally.`;
    }
  }

  if (loading || !brsJobData) {
    return (
      <div className={classes.loadingOverlay} style={{ position: 'relative', minHeight: 240 }}>
        <CircularProgress color="inherit" />
        <p style={{ marginTop: 12 }}>Loading traffic update…</p>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <a className={classes.breadcrumbLink} href="#/brs">
        {'< BRS Jobs'}
      </a>

      <div className={classes.content}>
        <h2
          style={{
            margin: '0 0 16px',
            fontSize: 18,
            fontWeight: 600,
            color: 'inherit',
          }}>
          Traffic Update — {brsJobData.request_id}
        </h2>

        <NewRequestSection
          formData={formData}
          onChange={handleFormDataChange}
          inventorySources={inventorySources}
          readOnly
        />

        <BroadcastRequestsSection
          spots={spots}
          onSpotsChange={setSpots}
          unitRotation={formData.unit_rotation}
          brandChoices={brandChoices}
          createSpot={createSpot}
          flightStartDate={formData.flight_start_date}
          flightEndDate={formData.flight_end_date}
        />

        <WeeklyRotationSummary
          spots={spots}
          onSpotsChange={setSpots}
          unitRotation={formData.unit_rotation}
          acknowledgedZeroDays={acknowledgedZeroDays}
          onToggleZeroDay={toggleZeroDayAck}
        />

        {antennaSDActive && (
          <div className={classes.sdBanner}>
            <span>&#9888;&#65039;</span>
            <span>
              <strong>Antenna requires SD.</strong> Antenna-SD is a destination on this job.
            </span>
          </div>
        )}

        <DestinationsSection
          selectedDestinations={selectedDestinations}
          onDestinationsChange={setSelectedDestinations}
          onAntennaSDChange={setAntennaSDActive}
        />

        <FormFooter
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          onGeneratePdf={handleGeneratePdf}
          onAttachPdf={handleAttachPdf}
          onRemoveAttachedPdf={handleRemoveAttachedPdf}
          attachedPdf={attachedPdfFile}
          isSubmitting={isSubmitting}
          submitBlocked={submitBlocked}
          submitBlockedReason={submitBlockedReason}
        />
      </div>

      <PDFPreviewModal
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        pdfUrl={pdfUrl}
        onSend={handlePdfSend}
        isLoading={isSubmitting}
      />

      {isSubmitting && (
        <div className={classes.loadingOverlay}>
          <CircularProgress color="inherit" />
          <p style={{ marginTop: 12 }}>Please wait while we process your request.</p>
        </div>
      )}
    </div>
  );
};
